import { Fragment } from 'react';
import type { ReactNode } from 'react';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import SelectableQuantityCounter from '../../../../components/ui/SelectableQuantityCounter/SelectableQuantityCounter';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { LotteryOptionGoodsForm } from '../../types';
import styles from './OptionSelectableCollectionList.module.scss';

type OptionSelectableCollectionListProps = {
  formItem: LotteryOptionGoodsForm;
  selectableItems: NonNullable<LotteryOptionGoodsForm['optionInfo']['selectable']>;
  isDeadlinePassed: boolean;
  selectedItems: Map<string, number>;
  stagingHeader: (formItem: LotteryOptionGoodsForm) => ReactNode;
};

function OptionSelectableCollectionList({
  formItem,
  selectableItems,
  isDeadlinePassed,
  selectedItems,
  stagingHeader,
}: OptionSelectableCollectionListProps) {
  const renderSelectableItem = (
    selectableItem: NonNullable<LotteryOptionGoodsForm['optionInfo']['selectable']>[number],
    index: number,
  ) => {
    const isFirstSelectableItem: boolean = index === 0;

    // 自動入力数を表示するかどうかを判定する関数
    const isAutoInput = () => {
      // 入力期限を迎えていない場合は非表示
      if (!isDeadlinePassed) return false;

      // システムインプットがされていない場合は非表示
      if (!selectableItem.isSystemInput) return false;

      return true;
    };

    // 期限切れの場合のスタイルを取得する関数
    const getWrapperQuantityPastDeadlineClassName = () => {
      if (isAutoInput() || isDeadlinePassed) return styles.wrapperQuantityPastDeadline;
      return '';
    };

    const selectableImageUrl = 'imageUrl' in selectableItem ? selectableItem.imageUrl : undefined;

    return (
      // 選べるオプションUIは、ヘッダーとカード本体の２種類の要素を返しているので、Fragmentでラップすることで、一塊のDOM要素として扱うことができる
      // divでラップすると、DOMの階層が増えてしまい、今後のCSS追加が大変になるため、Fragmentでラップする方針とする。
      <Fragment key={`selectable-group-${formItem.id}-${index}`}>
        {isFirstSelectableItem ? (
          <div
            key={`selectable-header-${formItem.id}`}
            className={`${styles.itemCardHeaderQuantity} ${styles.optionItemGroupQuantityHeaderItem}`}
          >
            <div className={styles.itemCardHeaderQuantityInner}>
              <div className={`${styles.itemDetailHeaderQuantityWrapper}`}>
                <div className={`${styles.itemDetailHeaderQuantity} ${utilsStyles.mlSmall}`}>
                  <div className={styles.stagingRankHeaderQuantityWrapper}>{stagingHeader(formItem)}</div>
                  <div className={styles.itemNameHeaderQuantityWrapper}>
                    <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>{formItem.label.name}</p>
                  </div>
                  <div className={styles.itemNameHeaderQuantityWrapper}>
                    <p className={`${styles.itemNameHeaderQuantity} ${utilsStyles.textBold}`}>{formItem.name}</p>
                  </div>
                  <div className={styles.acquiredCountHeaderQuantityWrapper}>
                    <p className={`${styles.acquiredCountHeaderQuantity}`}>保有数：{formItem.totalAcquiredCount}個</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div
          key={`selectable-${formItem.id}-${index}`}
          className={`${styles.itemCardQuantity} ${styles.optionItemGroupQuantityItem}`}
        >
          <div className={styles.itemCardQuantityInner}>
            <div className={`${styles.itemDetailQuantityWrapper} ${utilsStyles.mtXSmall}`}>
              <div className={styles.itemImageQuantity}>
                <ImageView alt={selectableItem.label || formItem.name} src={selectableImageUrl || formItem.imageUrl} />
              </div>
              <div className={`${styles.itemDetailQuantity} ${utilsStyles.mlSmall}`}>
                <div className={styles.itemNameWrapper}>
                  <p className={`${styles.itemNameQuantity} ${utilsStyles.textBold}`}>
                    {selectableItem.label || 'オプション選択項目'}
                  </p>
                </div>
                <div className={`${styles.wrapperQuantity} ${getWrapperQuantityPastDeadlineClassName()}`}>
                  <div className={`${utilsStyles.centered} ${styles.quantityCounterWrapper}`}>
                    <SelectableQuantityCounter
                      goodId={`${formItem.id}-${index}`}
                      acquiredCount={formItem.totalAcquiredCount}
                      disabled={false}
                      tradeableInCount={formItem.totalAcquiredCount}
                      totalSelectedInLabel={Array.from(selectedItems.values()).reduce(
                        (sum, current) => sum + current,
                        0,
                      )}
                      initialQuantity={selectableItem.value ? Number(selectableItem.value) : 0}
                      isPastDeadline={isDeadlinePassed}
                      isAutoInput={isAutoInput()}
                    />
                  </div>
                </div>
                <div className={styles.autoInputMessageQuantityWrapper}>
                  {isAutoInput() && (
                    <div
                      className={`${utilsStyles.textSmall} ${utilsStyles.textBold} ${styles.autoInputMessage} ${styles.autoInputMessageRight}`}
                    >
                      自動入力数：{selectableItem.value}個
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <>
      {selectableItems.map(renderSelectableItem)}
    </>
  );
}

export default OptionSelectableCollectionList;
